import { useReducer, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CBadge, CButton } from "@coreui/react-pro";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkDirective from "remark-directive";
import rehypeSlug from "rehype-slug";
import { HelpArticleNotFound } from "./HelpArticleNotFound";
import { categoryLabel } from "./categories";
import { adjacentHelpArticles, getHelpArticle } from "./help-content";
import { useHelpArticles } from "./useHelpArticles";
import { markdownComponents } from "./markdown-components";
import { remarkCallouts } from "./remark-callouts";
import { remarkMarks } from "./remark-marks";
import { remarkExampleCard } from "./remark-example-card";
import { HelpToc } from "./HelpToc";
import { helpArticlePath, routePaths } from "../../routes/route-paths";
import { trackEvent } from "../../lib/telemetry";
import { usePortalSettings } from "../../api/hooks/usePortalSettings";

// Raw HTML in an article is deliberately NOT rendered. react-markdown ignores
// it by default, which is safe with no sanitiser to configure and no bundle
// cost — enabling it needed rehype-raw plus rehype-sanitize, ~180kB for an
// escape hatch nothing has asked for yet. Everything an article needs comes
// from the component map and the callout directives instead. If a genuine case
// turns up, add both back together — rehype-raw without a sanitiser is an XSS.
const REMARK_PLUGINS = [
  remarkGfm,
  remarkDirective,
  remarkCallouts,
  remarkMarks,
  remarkExampleCard,
];
const REHYPE_PLUGINS = [rehypeSlug];

// Module scope rather than component state: the component is reused across slugs, and an article
// answered once should not ask again for the rest of the visit.
const answeredArticles = new Set<string>();

export function HelpArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const articleRef = useRef<HTMLDivElement>(null);
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  const settings = usePortalSettings();
  const articles = useHelpArticles();
  const article = articles.find((a) => a.slug === slug) ?? null;

  // A link to an article behind a setting waits for the setting rather than flashing "not found".
  if (!article && getHelpArticle(slug)?.requires && settings.isPending) {
    return null;
  }

  // An unknown slug lands somewhere useful rather than dead-ending, but stays
  // on the URL rather than redirecting — a silent bounce to the index would
  // make every mistyped tooltip look like a working one.
  if (!article) {
    return <HelpArticleNotFound slug={slug} />;
  }

  const { previous, next } = adjacentHelpArticles(articles, article.slug);
  const feedbackGiven = answeredArticles.has(article.slug);

  const giveFeedback = (helpful: boolean) => {
    answeredArticles.add(article.slug);
    trackEvent("HelpArticleFeedback", { slug: article.slug, helpful });
    rerender();
  };

  return (
    <div className="help-article-layout">
      {/* The header sits in its own grid row so the table of contents can start level
          with the rule beneath it. Nudging the TOC down with a fixed padding would
          drift the moment a title wraps onto two lines. */}
      <div className="help-article-header">
        <Link
          to={routePaths.help}
          className="btn btn-sm btn-outline-secondary mb-4"
        >
          {t("help:article.backToIndex")}
        </Link>

        <div className="mb-2">
          <CBadge
            className="help-category-badge bg-primary-subtle text-primary"
            shape="rounded-pill"
          >
            {categoryLabel(article.category)}
          </CBadge>
        </div>

        <h1 className="mb-3">{article.title}</h1>

        <p className="help-article-meta font-monospace text-body-secondary">
          <span>
            {t("help:article.readTime", { count: article.readMinutes })}
          </span>
          <span aria-hidden="true">·</span>
          <span>{helpArticlePath(article.slug)}</span>
          <span aria-hidden="true">·</span>
          <span>{t("help:article.updated", { date: article.updated })}</span>
        </p>
      </div>

      <div className="help-article-page">
        <div className="help-article" ref={articleRef}>
          <Markdown
            remarkPlugins={REMARK_PLUGINS}
            rehypePlugins={REHYPE_PLUGINS}
            components={markdownComponents}
          >
            {article.body}
          </Markdown>
        </div>

        <div className="help-article-footer">
          <div className="d-flex align-items-center gap-2">
            {feedbackGiven ? (
              <span className="small text-success">
                {t("help:article.feedbackThanks")}
              </span>
            ) : (
              <>
                <span className="small text-body-secondary">
                  {t("help:article.feedbackPrompt")}
                </span>
                <CButton
                  size="sm"
                  color="secondary"
                  variant="outline"
                  onClick={() => giveFeedback(true)}
                >
                  {t("help:article.feedbackYes")}
                </CButton>
                <CButton
                  size="sm"
                  color="secondary"
                  variant="outline"
                  onClick={() => giveFeedback(false)}
                >
                  {t("help:article.feedbackNo")}
                </CButton>
              </>
            )}
          </div>

        </div>

        {/* Reading order runs across the whole set, so the last Getting started
            article leads into the first Automation one. */}
        {(previous || next) && (
          <nav
            className="help-article-nav"
            aria-label={t("help:article.articleNav")}
          >
            {previous && (
              <Link
                to={helpArticlePath(previous.slug)}
                className="help-step help-step-prev text-decoration-none"
              >
                <span className="help-step-label font-monospace">
                  {t("help:article.previous")}
                </span>
                <span>← {previous.title}</span>
              </Link>
            )}

            {next && (
              <Link
                to={helpArticlePath(next.slug)}
                className="help-step help-step-next text-decoration-none"
              >
                <span className="help-step-label font-monospace">
                  {t("help:article.next")}
                </span>
                <span>{next.title} →</span>
              </Link>
            )}
          </nav>
        )}
      </div>

      <HelpToc articleRef={articleRef} slug={article.slug} />
    </div>
  );
}
